import { useEffect, useRef, useState } from 'react';
import isEmail from 'validator/lib/isEmail';
import api from 'api';
import { Alert, Button, Link } from 'components/common';
import { Form, FormGroup, Text } from 'components/common/form';
import { useReduxState } from 'hooks';

const ResetPasswordForm = () => {
  const {
    messages,
    pageState: { passwordExpired },
    serverConfig: { accountEnableUsernameAccounts },
    theme
  } = useReduxState();

  const inputContainer = useRef<HTMLDivElement | null>(null);

  const [error, setError] = useState<string>(null);
  const [loading, setLoading] = useState(false);
  const [notActivated, setNotActivated] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (inputContainer.current) {
      inputContainer.current.querySelector('input').focus();
    }
  }, []);

  const submit = async ({ email }: { email: string }) => {
    try {
      if (!email || !isEmail(email)) {
        setError(messages.usersInvalidEmail);
        setLoading(false);
        return;
      }

      setError(null);
      setLoading(true);
      setSuccess(false);

      const response = await api.post('/auth/local/reset-password', { email });

      if (response.data?.info === 'user_not_activated') {
        setNotActivated(true);
      } else if (response.data?.success) {
        setSuccess(true);
      } else if (response.data?.error === 'user_not_found') {
        setError(messages.authResetPasswordUserNotFound);
      } else if (response.data?.error === 'user_signed_in_with_sso') {
        setError(messages.authResetPasswordSingleSignOnUser);
      } else if (response.data?.error === 'account_expired') {
        setError(messages.authAccountExpired);
      } else if (response.data?.error === 'account_locked') {
        setError(messages.authAccountLocked);
      } else if (response.data?.error === 'invalid_email') {
        setError(messages.usersInvalidEmail);
      } else {
        setError(messages.commonError);
      }
    } catch (err) {
      setError(messages.commonError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-password-form">
      {passwordExpired ? (
        <Alert
          style={{ marginBottom: 15 }}
          text={messages.authPasswordExpired}
          type="error-light"
        />
      ) : null}

      <div className="info">{`${messages.authResetPasswordInfo} ${
        accountEnableUsernameAccounts
          ? messages.authResetPasswordNoEmailInfo
          : ''
      }`}</div>

      <Form onSubmit={submit}>
        <FormGroup htmlFor="email" label={messages.commonEmail}>
          <Text
            field="email"
            id="email"
            inputContainerRef={(el: HTMLDivElement) =>
              (inputContainer.current = el)
            }
            type="text"
          />
        </FormGroup>

        <Button
          category="success"
          disabled={notActivated || success}
          loading={loading}
          text={messages.commonSend}
          type="submit"
        />

        {error ? (
          <Alert
            className="reset-password-alert"
            style={{ marginTop: 15 }}
            text={error}
            type="error-light"
          />
        ) : notActivated ? (
          <Alert
            className="reset-password-alert"
            style={{ marginTop: 15 }}
            text={messages.authResetPasswordNotActivated}
          />
        ) : success ? (
          <Alert
            className="reset-password-alert"
            style={{ marginTop: 15 }}
            text={messages.authResetPasswordSuccess}
            type="success-light"
          />
        ) : null}
      </Form>

      <div className="login">
        <Link href="/login" text={messages.authBackToLogin} />
      </div>

      <style jsx>{`
        .reset-password-form :global(button) {
          margin-top: 15px;
          width: 100%;
        }
        .info {
          color: ${theme.textColorLight};
          font-size: 1.4rem;
          font-weight: 600;
          margin-bottom: 10px;
        }
        .login {
          font-size: 1.4rem;
          margin-top: 15px;
          text-align: center;
        }
      `}</style>
    </div>
  );
};

export default ResetPasswordForm;
